import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../firebase';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import './PromptManagementPage.css';
import StyleLogoEditor from '../components/StyleLogoEditor';
import { getLogoForStyle } from '../utils/styleLogos';
import { DEFAULT_LOGO_STYLE, normalizeLogoStyleConfig } from '../utils/logoOverlayConfig';

const DEFAULT_CONTOUR_PROMPT = "Change the map into a contour sketch. Make sure to keep the orange line. Add text 'contour' in the bottom left corner.";
const PROMPT_TEMPLATE_CACHE_CONTROL = 'public,max-age=86400';
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // 50MB ceiling for short transition clips

// Video previews use object URLs; revoke them (never the Firestore download URLs) to avoid leaks.
const revokeObjectUrl = (url) => {
    if (typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
    }
};

function PromptManagementPage({ user }) { // User prop for potential auth checks
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showForm, setShowForm] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(null); // For new or editing template
    // Tag states
    const [tags, setTags] = useState([]);
    const [tagCategories, setTagCategories] = useState({});
    // Form field states
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [templateString, setTemplateString] = useState('');
    const [requiresStats, setRequiresStats] = useState(false);
    const [statsValidationError, setStatsValidationError] = useState(''); // New state for stats validation
    const [coastalOnly, setCoastalOnly] = useState(false);
    const [landOnly, setLandOnly] = useState(false);
    const [adminOnly, setAdminOnly] = useState(false);
    const [testInParallel, setTestInParallel] = useState(false); // New state for parallel testing
    const [mapboxStyle, setMapboxStyle] = useState(''); // New state for Mapbox style
    const [generationModel, setGenerationModel] = useState('gpt-image-1'); // New state for generation model
    const defaultModelRef = useRef('gemini-3-flash'); // House default for new templates, refreshed from settings/defaults
    const [contourFirst, setContourFirst] = useState(false); // New state for contour preprocessing
    const [contourPrompt, setContourPrompt] = useState(DEFAULT_CONTOUR_PROMPT);
    const [contourModel, setContourModel] = useState('flux');
    const [mapInputType, setMapInputType] = useState('2d');
    const [variantOf, setVariantOf] = useState('');
    const contourModelShortName = contourModel === 'gemini' ? 'Gemini' : 'Flux';

    // Image upload states (example image - landscape)
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState('');

    // Image upload states (example image - portrait)
    const [selectedPortraitImage, setSelectedPortraitImage] = useState(null);
    const [portraitImagePreview, setPortraitImagePreview] = useState('');
    const [isUploadingPortraitImage, setIsUploadingPortraitImage] = useState(false);
    const [currentPortraitImageUrl, setCurrentPortraitImageUrl] = useState('');

    // Video upload states (transition video - landscape)
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [videoPreview, setVideoPreview] = useState('');
    const [isUploadingVideo, setIsUploadingVideo] = useState(false);
    const [currentVideoUrl, setCurrentVideoUrl] = useState('');

    // Video upload states (transition video - portrait)
    const [selectedPortraitVideo, setSelectedPortraitVideo] = useState(null);
    const [portraitVideoPreview, setPortraitVideoPreview] = useState('');
    const [isUploadingPortraitVideo, setIsUploadingPortraitVideo] = useState(false);
    const [currentPortraitVideoUrl, setCurrentPortraitVideoUrl] = useState('');

    // Logo/icon upload states
    const [selectedLogo, setSelectedLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [currentLogoUrl, setCurrentLogoUrl] = useState('');
    const [logoStyle, setLogoStyle] = useState(() => normalizeLogoStyleConfig(DEFAULT_LOGO_STYLE));

    useEffect(() => {
        if (!user) { // Basic auth check, can be enhanced with admin roles later
            setError("You must be logged in to manage prompts.");
            setIsLoading(false);
            setTemplates([]);
            return;
        }
        setError(null); // Clear error if user becomes available
        setIsLoading(true);
        const templatesCollectionRef = collection(db, "promptTemplates");
        const unsubscribe = onSnapshot(templatesCollectionRef, (snapshot) => {
            const fetchedTemplates = [];
            snapshot.forEach(doc => {
                fetchedTemplates.push({ id: doc.id, ...doc.data() });
            });
            
            // Sort templates hierarchically
            const sortedTemplates = fetchedTemplates.sort((a, b) => {
                const aName = a.name || '';
                const bName = b.name || '';
                
                // Check if one is a prefix of the other (actual parent-child relationship)
                if (aName !== bName) {
                    // Check if b is a child of a (a is prefix of b)
                    if (bName.startsWith(aName + ' ')) {
                        return -1; // a (parent) comes before b (child)
                    }
                    // Check if a is a child of b (b is prefix of a)
                    if (aName.startsWith(bName + ' ')) {
                        return 1; // b (parent) comes before a (child)
                    }
                }
                
                // Neither is a parent of the other, sort alphabetically
                return aName.localeCompare(bName);
            });
            
            setTemplates(sortedTemplates);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching prompt templates:", err);
            setError("Failed to load prompt templates.");
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [user]);

    // Fetch tag definitions from Firestore
    useEffect(() => {
        const loadTagCategories = async () => {
            try {
                const tagDoc = await getDoc(doc(db, "settings", "styleTags"));
                if (tagDoc.exists() && tagDoc.data().categories) {
                    setTagCategories(tagDoc.data().categories);
                }
            } catch (err) {
                console.error("Error fetching tag categories:", err);
            }
        };
        loadTagCategories();
    }, []);

    // House default model for new templates, shared with the admin API (settings/defaults.imageModel)
    useEffect(() => {
        const loadDefaultModel = async () => {
            try {
                const defaultsDoc = await getDoc(doc(db, "settings", "defaults"));
                const model = defaultsDoc.exists() ? defaultsDoc.data().imageModel : null;
                if (typeof model === 'string' && model.trim()) {
                    defaultModelRef.current = model.trim();
                }
            } catch (err) {
                console.error("Error fetching default model:", err);
            }
        };
        loadDefaultModel();
    }, []);

    const resetFormState = () => {
        setCurrentTemplate(null);
        setError(null);
        setStatsValidationError('');
        setSelectedImage(null);
        setImagePreview('');
        setCurrentImageUrl('');
        setSelectedPortraitImage(null);
        setPortraitImagePreview('');
        setCurrentPortraitImageUrl('');
        setSelectedVideo(null);
        setVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        setCurrentVideoUrl('');
        setSelectedPortraitVideo(null);
        setPortraitVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        setCurrentPortraitVideoUrl('');
        setSelectedLogo(null);
        setLogoPreview('');
        setCurrentLogoUrl('');
        setLogoStyle(normalizeLogoStyleConfig(DEFAULT_LOGO_STYLE));
        setCoastalOnly(false);
        setLandOnly(false);
        setAdminOnly(false);
        setTestInParallel(false);
        setMapboxStyle('');
        setGenerationModel('gpt-image-1');
        setContourFirst(false);
        setContourPrompt(DEFAULT_CONTOUR_PROMPT);
        setContourModel('flux');
        setMapInputType('2d');
        setVariantOf('');
        setRequiresStats(false);
        setTemplateString('');
        setName('');
        setDescription('');
        setTags([]);
    };

    const closeForm = () => {
        setShowForm(false);
        resetFormState();
    };

    const handleEdit = (template) => {
        setCurrentTemplate(template);
        setName(template.name || '');
        setDescription(template.description || '');
        setTemplateString(template.templateString || '');
        setRequiresStats(template.requiresStravaStats || false);
        setCoastalOnly(template.coastalOnly || false);
        setLandOnly(template.landOnly || false);
        setAdminOnly(template.adminOnly || false);
        setTestInParallel(template.testInParallel || false);
        setMapboxStyle(template.mapboxStyle || '');
        setGenerationModel(template.generationModel || 'gpt-image-1');
        setContourFirst(template.contourFirst || false);
        setContourPrompt(template.contourPrompt || DEFAULT_CONTOUR_PROMPT);
        setContourModel(template.contourModel || 'flux');
        setMapInputType(template.mapInputType || '2d');
        setVariantOf(template.variantOf || '');
        setCurrentImageUrl(template.exampleImageUrl || '');
        setImagePreview(template.exampleImageUrl || '');
        setSelectedImage(null);
        setCurrentPortraitImageUrl(template.portraitExampleImageUrl || '');
        setPortraitImagePreview(template.portraitExampleImageUrl || '');
        setSelectedPortraitImage(null);
        setCurrentVideoUrl(template.transitionVideoUrl || '');
        setVideoPreview((prev) => { revokeObjectUrl(prev); return template.transitionVideoUrl || ''; });
        setSelectedVideo(null);
        setCurrentPortraitVideoUrl(template.portraitTransitionVideoUrl || '');
        setPortraitVideoPreview((prev) => { revokeObjectUrl(prev); return template.portraitTransitionVideoUrl || ''; });
        setSelectedPortraitVideo(null);
        setCurrentLogoUrl(template.logoUrl || '');
        setLogoPreview(template.logoUrl || '');
        setSelectedLogo(null);
        setLogoStyle(normalizeLogoStyleConfig(template.logoStyle));
        setTags(template.tags || []);
        setShowForm(true);
    };

    const handleCreateNew = () => {
        setCurrentTemplate(null); // No ID means it's a new one
        setName('');
        setDescription('');
        setTemplateString(
            'Task:\n\n- ...\n\nStrict Technical Constraints:\n\n- ...\n\nStyling Instructions:\n\n- ...\n\n{{#STATS}}\nAdditional Element:\n\n- Title: {{TITLE}}\n- Distance: {{DISTANCE_KM}}km\n- Duration: {{DURATION_HMS}}\n- Power: {{POWER_W}}W\n- Elevation Gain: {{ELEVATION_M}}m\n\n{{/STATS}}Important:\n\n- ...\n\nPriority Order:\n\n1. ...'
        );
        setRequiresStats(true);
        setCoastalOnly(false);
        setLandOnly(false);
        setAdminOnly(true); // Default to admin only for new prompts
        setTestInParallel(false);
        setMapboxStyle(''); // Leave empty to use default
        setGenerationModel(defaultModelRef.current); // House default from settings/defaults (Gemini 3.1 Flash Image unless changed)
        setContourFirst(false);
        setContourPrompt(DEFAULT_CONTOUR_PROMPT);
        setContourModel('flux');
        setMapInputType('2d');
        setVariantOf('');
        setTags([]);
        setCurrentImageUrl('');
        setImagePreview('');
        setSelectedImage(null);
        setCurrentPortraitImageUrl('');
        setPortraitImagePreview('');
        setSelectedPortraitImage(null);
        setCurrentVideoUrl('');
        setVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        setSelectedVideo(null);
        setCurrentPortraitVideoUrl('');
        setPortraitVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        setSelectedPortraitVideo(null);
        setCurrentLogoUrl('');
        setLogoPreview('');
        setSelectedLogo(null);
        setLogoStyle(normalizeLogoStyleConfig(DEFAULT_LOGO_STYLE));
        setShowForm(true);
    };

    const handleDuplicate = (template) => {
        setCurrentTemplate(null); // No ID means it's a new one
        setName(template.name + ' Copy');
        setDescription(template.description || '');
        setTemplateString(template.templateString || '');
        setRequiresStats(template.requiresStravaStats || false);
        setCoastalOnly(template.coastalOnly || false);
        setLandOnly(template.landOnly || false);
        setAdminOnly(true); // Always default to admin only for duplicates
        setTestInParallel(template.testInParallel || false);
        setMapboxStyle(template.mapboxStyle || '');
        setGenerationModel(template.generationModel || 'gpt-image-1');
        setContourFirst(template.contourFirst || false);
        setContourPrompt(template.contourPrompt || DEFAULT_CONTOUR_PROMPT);
        setContourModel(template.contourModel || 'flux');
        setMapInputType(template.mapInputType || '2d');
        setVariantOf(template.variantOf || '');
        setTags(template.tags || []);
        setCurrentImageUrl('');
        setImagePreview('');
        setSelectedImage(null);
        setCurrentPortraitImageUrl('');
        setPortraitImagePreview('');
        setSelectedPortraitImage(null);
        setCurrentVideoUrl('');
        setVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        setSelectedVideo(null);
        setCurrentPortraitVideoUrl('');
        setPortraitVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        setSelectedPortraitVideo(null);
        setCurrentLogoUrl('');
        setLogoPreview('');
        setSelectedLogo(null);
        setLogoStyle(normalizeLogoStyleConfig(template.logoStyle || DEFAULT_LOGO_STYLE));
        setShowForm(true);
    };

    const handleDelete = async (templateId) => {
        if (window.confirm("Are you sure you want to delete this prompt template?")) {
            try {
                // Find the template to get its image URL for deletion
                const templateToDelete = templates.find(t => t.id === templateId);

                // Delete the document
                await deleteDoc(doc(db, "promptTemplates", templateId));

                // Delete the associated media files if they exist. Each is best-effort so a
                // missing object never blocks the delete.
                const mediaUrlsToDelete = [
                    templateToDelete?.exampleImageUrl,
                    templateToDelete?.portraitExampleImageUrl,
                    templateToDelete?.transitionVideoUrl,
                    templateToDelete?.portraitTransitionVideoUrl,
                ].filter(Boolean);

                for (const mediaUrl of mediaUrlsToDelete) {
                    try {
                        await deleteObject(ref(storage, mediaUrl));
                    } catch (mediaError) {
                        console.warn("Could not delete associated media file:", mediaUrl, mediaError);
                        // Don't fail the whole operation if media deletion fails
                    }
                }

                // List will update via onSnapshot
            } catch (err) {
                console.error("Error deleting template:", err);
                setError("Failed to delete template: " + err.message);
            }
        }
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError("Please select a valid image file.");
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError("Image file size must be less than 5MB.");
                return;
            }

            setSelectedImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview('');
        // Don't clear currentImageUrl here - that's for existing images
    };

    const handlePortraitImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError("Please select a valid image file.");
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > MAX_IMAGE_BYTES) {
                setError("Image file size must be less than 5MB.");
                return;
            }

            setSelectedPortraitImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setPortraitImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePortraitImage = () => {
        setSelectedPortraitImage(null);
        setPortraitImagePreview('');
        // Don't clear currentPortraitImageUrl here - that's for existing images
    };

    const handleVideoSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('video/')) {
                setError("Please select a valid video file.");
                return;
            }

            // Validate file size (max 50MB)
            if (file.size > MAX_VIDEO_BYTES) {
                setError("Video file size must be less than 50MB.");
                return;
            }

            setSelectedVideo(file);
            // Use an object URL for the preview to avoid base64-encoding a large file into state
            setVideoPreview((prev) => { revokeObjectUrl(prev); return URL.createObjectURL(file); });
        }
    };

    const handleRemoveVideo = () => {
        setSelectedVideo(null);
        setVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        // Don't clear currentVideoUrl here - that's for existing videos
    };

    const handlePortraitVideoSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('video/')) {
                setError("Please select a valid video file.");
                return;
            }

            // Validate file size (max 50MB)
            if (file.size > MAX_VIDEO_BYTES) {
                setError("Video file size must be less than 50MB.");
                return;
            }

            setSelectedPortraitVideo(file);
            // Use an object URL for the preview to avoid base64-encoding a large file into state
            setPortraitVideoPreview((prev) => { revokeObjectUrl(prev); return URL.createObjectURL(file); });
        }
    };

    const handleRemovePortraitVideo = () => {
        setSelectedPortraitVideo(null);
        setPortraitVideoPreview((prev) => { revokeObjectUrl(prev); return ''; });
        // Don't clear currentPortraitVideoUrl here - that's for existing videos
    };

    const handleLogoSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError("Please select a valid image file for the logo.");
                return;
            }

            // Validate file size (max 2MB for logos)
            if (file.size > 2 * 1024 * 1024) {
                setError("Logo file size must be less than 2MB.");
                return;
            }

            setSelectedLogo(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveLogo = () => {
        setSelectedLogo(null);
        setLogoPreview('');
        // Don't clear currentLogoUrl here - that's for existing logos
    };

    const uploadLogo = async (templateId) => {
        if (!selectedLogo) return currentLogoUrl;

        setIsUploadingLogo(true);
        try {
            // Create a reference to the logo location
            const logoRef = ref(storage, `prompt-templates/${templateId}/logo.${selectedLogo.name.split('.').pop()}`);

            // Upload the logo
            await uploadBytes(logoRef, selectedLogo, {
                cacheControl: PROMPT_TEMPLATE_CACHE_CONTROL,
                contentType: selectedLogo.type || undefined
            });

            // Get the download URL
            const downloadURL = await getDownloadURL(logoRef);

            return downloadURL;
        } catch (error) {
            console.error("Error uploading logo:", error);
            throw new Error("Failed to upload logo: " + error.message);
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const uploadImage = async (templateId) => {
        if (!selectedImage) return currentImageUrl;

        setIsUploadingImage(true);
        try {
            // Create a reference to the image location
            const imageRef = ref(storage, `prompt-templates/${templateId}/example.${selectedImage.name.split('.').pop()}`);

            // Upload the image
            await uploadBytes(imageRef, selectedImage, {
                cacheControl: PROMPT_TEMPLATE_CACHE_CONTROL,
                contentType: selectedImage.type || undefined
            });

            // Get the download URL
            const downloadURL = await getDownloadURL(imageRef);

            return downloadURL;
        } catch (error) {
            console.error("Error uploading image:", error);
            throw new Error("Failed to upload image: " + error.message);
        } finally {
            setIsUploadingImage(false);
        }
    };

    const uploadPortraitImage = async (templateId) => {
        if (!selectedPortraitImage) return currentPortraitImageUrl;

        setIsUploadingPortraitImage(true);
        try {
            const imageRef = ref(storage, `prompt-templates/${templateId}/example-portrait.${selectedPortraitImage.name.split('.').pop()}`);

            await uploadBytes(imageRef, selectedPortraitImage, {
                cacheControl: PROMPT_TEMPLATE_CACHE_CONTROL,
                contentType: selectedPortraitImage.type || undefined
            });

            const downloadURL = await getDownloadURL(imageRef);

            return downloadURL;
        } catch (error) {
            console.error("Error uploading portrait image:", error);
            throw new Error("Failed to upload portrait image: " + error.message);
        } finally {
            setIsUploadingPortraitImage(false);
        }
    };

    const uploadVideo = async (templateId) => {
        if (!selectedVideo) return currentVideoUrl;

        setIsUploadingVideo(true);
        try {
            const videoRef = ref(storage, `prompt-templates/${templateId}/transition-landscape.${selectedVideo.name.split('.').pop()}`);

            await uploadBytes(videoRef, selectedVideo, {
                cacheControl: PROMPT_TEMPLATE_CACHE_CONTROL,
                contentType: selectedVideo.type || undefined
            });

            const downloadURL = await getDownloadURL(videoRef);

            return downloadURL;
        } catch (error) {
            console.error("Error uploading video:", error);
            throw new Error("Failed to upload video: " + error.message);
        } finally {
            setIsUploadingVideo(false);
        }
    };

    const uploadPortraitVideo = async (templateId) => {
        if (!selectedPortraitVideo) return currentPortraitVideoUrl;

        setIsUploadingPortraitVideo(true);
        try {
            const videoRef = ref(storage, `prompt-templates/${templateId}/transition-portrait.${selectedPortraitVideo.name.split('.').pop()}`);

            await uploadBytes(videoRef, selectedPortraitVideo, {
                cacheControl: PROMPT_TEMPLATE_CACHE_CONTROL,
                contentType: selectedPortraitVideo.type || undefined
            });

            const downloadURL = await getDownloadURL(videoRef);

            return downloadURL;
        } catch (error) {
            console.error("Error uploading portrait video:", error);
            throw new Error("Failed to upload portrait video: " + error.message);
        } finally {
            setIsUploadingPortraitVideo(false);
        }
    };

    const syncLinked3DLogoSettings = async (baseTemplateId, syncedLogoStyle, syncedLogoUrl) => {
        if (!baseTemplateId) {
            return;
        }

        const linked3DTemplates = templates.filter((template) =>
            (template.mapInputType || '2d') === '3d' &&
            template.variantOf === baseTemplateId
        );

        if (linked3DTemplates.length === 0) {
            return;
        }

        await Promise.all(
            linked3DTemplates.map((template) =>
                updateDoc(doc(db, "promptTemplates", template.id), {
                    logoStyle: syncedLogoStyle,
                    logoUrl: syncedLogoUrl,
                    updatedAt: serverTimestamp()
                })
            )
        );
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setStatsValidationError(''); // Clear previous stats validation error

        if (!name.trim() || !templateString.trim()) {
            setError("Name and Template String are required.");
            return;
        }

        if (requiresStats) {
            const missingElements = [];
            const hasStatsBlockStart = templateString.includes("{{#STATS}}");
            const hasStatsBlockEnd = templateString.includes("{{/STATS}}"); // Check for the literal string

            if (!hasStatsBlockStart) {
                missingElements.push("{{#STATS}}");
            }
            if (!hasStatsBlockEnd) {
                missingElements.push("{{/STATS}}");
            }

            const requiredPlaceholders = ["{{TITLE}}", "{{DISTANCE_KM}}", "{{DURATION_HMS}}", "{{POWER_W}}", "{{ELEVATION_M}}"];

            if (hasStatsBlockStart && hasStatsBlockEnd) {
                const statsBlockRegex = /{{#STATS}}([\s\S]*?){{\/STATS}}/; // Corrected Regex
                const statsBlockMatch = templateString.match(statsBlockRegex);
                // statsBlockMatch[0] is the full match, statsBlockMatch[1] is the content inside
                const statsBlockContent = statsBlockMatch && statsBlockMatch[1] ? statsBlockMatch[1] : "";

                requiredPlaceholders.forEach(placeholder => {
                    if (!statsBlockContent.includes(placeholder)) {
                        missingElements.push(placeholder + " (must be within {{#STATS}}...{{/STATS}} block)");
                    }
                });
            } else {
                // If either block marker is missing, all placeholders are effectively missing from *within* the block
                // but we only add them if the block markers themselves weren't already added.
                requiredPlaceholders.forEach(placeholder => {
                    if (!missingElements.includes("{{#STATS}}") && !missingElements.includes("{{/STATS}}")) {
                        missingElements.push(placeholder + " ({{#STATS}}...{{/STATS}} block is incomplete or missing)");
                    }
                });
            }

            const uniqueMissingElements = [...new Set(missingElements)];

            if (uniqueMissingElements.length > 0) {
                setStatsValidationError(`When 'Requires Strava Stats' is checked, the following are missing or incorrect in the template: ${uniqueMissingElements.join(', ')}`);
                return;
            }
        }

        setError(null);

        try {
            let templateId;
            let imageUrl = currentImageUrl;
            let portraitImageUrl = currentPortraitImageUrl;
            let videoUrl = currentVideoUrl;
            let portraitVideoUrl = currentPortraitVideoUrl;
            let logoUrl = currentLogoUrl;
            const normalizedLogoStyle = normalizeLogoStyleConfig(logoStyle);

            // First, save the template to get an ID if it's new
            const dataToSave = {
                name,
                description,
                templateString,
                requiresStravaStats: requiresStats,
                coastalOnly: coastalOnly,
                landOnly: landOnly,
                adminOnly: adminOnly,
                testInParallel: testInParallel,
                mapboxStyle: mapboxStyle,
                generationModel: generationModel,
                contourFirst: contourFirst,
                contourPrompt: contourPrompt,
                contourModel: contourModel,
                mapInputType: mapInputType,
                variantOf: mapInputType === '3d' && variantOf ? variantOf : null,
                logoStyle: normalizedLogoStyle,
                tags: tags,
                updatedAt: serverTimestamp()
            };

            if (currentTemplate && currentTemplate.id) {
                // Update existing
                templateId = currentTemplate.id;

                // Upload new image if selected
                if (selectedImage) {
                    imageUrl = await uploadImage(templateId);
                }

                // Upload new portrait image if selected
                if (selectedPortraitImage) {
                    portraitImageUrl = await uploadPortraitImage(templateId);
                }

                // Upload new transition videos if selected
                if (selectedVideo) {
                    videoUrl = await uploadVideo(templateId);
                }
                if (selectedPortraitVideo) {
                    portraitVideoUrl = await uploadPortraitVideo(templateId);
                }

                // Upload new logo if selected
                if (selectedLogo) {
                    logoUrl = await uploadLogo(templateId);
                }

                // Update with image, video and logo URLs
                await updateDoc(doc(db, "promptTemplates", templateId), {
                    ...dataToSave,
                    exampleImageUrl: imageUrl,
                    portraitExampleImageUrl: portraitImageUrl,
                    transitionVideoUrl: videoUrl,
                    portraitTransitionVideoUrl: portraitVideoUrl,
                    logoUrl: logoUrl
                });
            } else {
                // Create new
                const docRef = await addDoc(collection(db, "promptTemplates"), {
                    ...dataToSave,
                    createdAt: serverTimestamp()
                });
                templateId = docRef.id;

                // Upload image if selected
                if (selectedImage) {
                    imageUrl = await uploadImage(templateId);
                }

                // Upload portrait image if selected
                if (selectedPortraitImage) {
                    portraitImageUrl = await uploadPortraitImage(templateId);
                }

                // Upload transition videos if selected
                if (selectedVideo) {
                    videoUrl = await uploadVideo(templateId);
                }
                if (selectedPortraitVideo) {
                    portraitVideoUrl = await uploadPortraitVideo(templateId);
                }

                // Upload logo if selected
                if (selectedLogo) {
                    logoUrl = await uploadLogo(templateId);
                }

                // Update the document with any uploaded media URLs
                if (selectedImage || selectedPortraitImage || selectedVideo || selectedPortraitVideo || selectedLogo) {
                    await updateDoc(doc(db, "promptTemplates", templateId), {
                        exampleImageUrl: imageUrl,
                        portraitExampleImageUrl: portraitImageUrl,
                        transitionVideoUrl: videoUrl,
                        portraitTransitionVideoUrl: portraitVideoUrl,
                        logoUrl: logoUrl
                    });
                }
            }

            const baseTemplateId = mapInputType === '3d' ? variantOf : templateId;
            await syncLinked3DLogoSettings(baseTemplateId, normalizedLogoStyle, logoUrl);

            closeForm();
        } catch (err) {
            console.error("Error saving template:", err);
            setError("Failed to save template: " + err.message);
        }
    };

    if (!user && !isLoading) { // If still loading auth, wait. If done and no user, show this.
        return (
            <div className="container prompt-management-page">
                <h2>Manage Prompts</h2>
                <p>Please <Link to="/">sign in</Link> to manage prompt templates.</p>
            </div>
        );
    }

    return (
        <div className="container prompt-management-page"> {/* Add a specific class for styling */}
            <h1>Manage Prompt Templates</h1>

            <button onClick={handleCreateNew} style={{ marginBottom: '20px' }} className="create-new-button"> {/* Added class */}
                Create New Prompt Template
            </button>

            {showForm && (
                <div className="modal-overlay" onClick={(e) => {
                    // Close modal if clicking on overlay (not content)
                    if (e.target.className === 'modal-overlay') {
                        closeForm();
                    }
                }}>
                    <div className="modal-content">
                        <button
                            className="modal-close"
                            type="button"
                            onClick={closeForm}
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <form onSubmit={handleFormSubmit} className="prompt-form"> {/* Removed form-section, handled by .prompt-form */}
                            <h2>{currentTemplate ? 'Edit' : 'Create'} Prompt Template</h2>
                    <div>
                        <label htmlFor="templateName">Name:</label>
                        <input type="text" id="templateName" value={name} onChange={(e) => setName(e.target.value)} required />
                    </div>
                    {/* Tags */}
                    <div style={{ marginTop: '10px' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Tags:
                        </label>
                        {Object.keys(tagCategories).length > 0 ? (
                            Object.entries(tagCategories).map(([category, categoryTags]) => (
                                <div key={category} style={{ marginBottom: '10px', marginLeft: '10px' }}>
                                    <span style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                                        {category}
                                    </span>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {categoryTags.map(tag => {
                                            const isSelected = tags.includes(tag);
                                            return (
                                                <span
                                                    key={tag}
                                                    onClick={() => {
                                                        setTags(prev =>
                                                            isSelected
                                                                ? prev.filter(t => t !== tag)
                                                                : [...prev, tag]
                                                        );
                                                    }}
                                                    style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '16px',
                                                        fontSize: '13px',
                                                        cursor: 'pointer',
                                                        border: isSelected ? '1px solid #57534E' : '1px solid #ccc',
                                                        backgroundColor: isSelected ? '#57534E' : 'transparent',
                                                        color: isSelected ? '#fff' : '#333',
                                                        transition: 'all 0.15s ease',
                                                        userSelect: 'none',
                                                    }}
                                                >
                                                    {tag}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)', marginLeft: '10px', fontStyle: 'italic' }}>
                                No tag categories found. Add them in Firestore at settings/styleTags.
                            </p>
                        )}
                        {tags.length > 0 && (
                            <div style={{ marginTop: '8px', marginLeft: '10px' }}>
                                <span style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)' }}>
                                    Selected: {tags.join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="templateDesc">Description:</label>
                        <textarea id="templateDesc" value={description} onChange={(e) => setDescription(e.target.value)} rows="3"></textarea>
                    </div>
                    <div>
                        <label htmlFor="exampleImage">Example Image (Landscape) (shows users what this style looks like):</label>
                        <input
                            type="file"
                            id="exampleImage"
                            accept="image/*"
                            onChange={handleImageSelect}
                            style={{ marginBottom: '10px' }}
                        />
                        {imagePreview && (
                            <div className="image-preview">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    style={{
                                        maxWidth: '300px',
                                        maxHeight: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    style={{
                                        marginLeft: '10px',
                                        padding: '5px 10px',
                                        fontSize: '12px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        {currentImageUrl && !imagePreview && (
                            <div className="current-image">
                                <p style={{ fontSize: '14px', color: 'var(--Gray-600, #475467)', margin: '5px 0' }}>
                                    Current image:
                                </p>
                                <img
                                    src={currentImageUrl}
                                    alt="Current example"
                                    style={{
                                        maxWidth: '300px',
                                        maxHeight: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="exampleImagePortrait">Example Image (Portrait) (used where a tall/portrait layout fits better):</label>
                        <input
                            type="file"
                            id="exampleImagePortrait"
                            accept="image/*"
                            onChange={handlePortraitImageSelect}
                            style={{ marginBottom: '10px' }}
                        />
                        {portraitImagePreview && (
                            <div className="image-preview">
                                <img
                                    src={portraitImagePreview}
                                    alt="Portrait preview"
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '300px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleRemovePortraitImage}
                                    style={{
                                        marginLeft: '10px',
                                        padding: '5px 10px',
                                        fontSize: '12px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        {currentPortraitImageUrl && !portraitImagePreview && (
                            <div className="current-image">
                                <p style={{ fontSize: '14px', color: 'var(--Gray-600, #475467)', margin: '5px 0' }}>
                                    Current portrait image:
                                </p>
                                <img
                                    src={currentPortraitImageUrl}
                                    alt="Current portrait example"
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '300px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="transitionVideoLandscape">Transition Video (Landscape) (map morphing into the artwork):</label>
                        <input
                            type="file"
                            id="transitionVideoLandscape"
                            accept="video/*"
                            onChange={handleVideoSelect}
                            style={{ marginBottom: '10px' }}
                        />
                        <p style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)', marginTop: '-5px', marginBottom: '10px' }}>
                            MP4 works best. Max 50MB.
                        </p>
                        {videoPreview && (
                            <div className="video-preview">
                                <video
                                    src={videoPreview}
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    style={{
                                        maxWidth: '300px',
                                        maxHeight: '200px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleRemoveVideo}
                                    style={{
                                        marginLeft: '10px',
                                        padding: '5px 10px',
                                        fontSize: '12px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        {currentVideoUrl && !videoPreview && (
                            <div className="current-video">
                                <p style={{ fontSize: '14px', color: 'var(--Gray-600, #475467)', margin: '5px 0' }}>
                                    Current landscape video:
                                </p>
                                <video
                                    src={currentVideoUrl}
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    style={{
                                        maxWidth: '300px',
                                        maxHeight: '200px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="transitionVideoPortrait">Transition Video (Portrait) (map morphing into the artwork):</label>
                        <input
                            type="file"
                            id="transitionVideoPortrait"
                            accept="video/*"
                            onChange={handlePortraitVideoSelect}
                            style={{ marginBottom: '10px' }}
                        />
                        <p style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)', marginTop: '-5px', marginBottom: '10px' }}>
                            MP4 works best. Max 50MB.
                        </p>
                        {portraitVideoPreview && (
                            <div className="video-preview">
                                <video
                                    src={portraitVideoPreview}
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '300px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleRemovePortraitVideo}
                                    style={{
                                        marginLeft: '10px',
                                        padding: '5px 10px',
                                        fontSize: '12px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        {currentPortraitVideoUrl && !portraitVideoPreview && (
                            <div className="current-video">
                                <p style={{ fontSize: '14px', color: 'var(--Gray-600, #475467)', margin: '5px 0' }}>
                                    Current portrait video:
                                </p>
                                <video
                                    src={currentPortraitVideoUrl}
                                    muted
                                    loop
                                    playsInline
                                    controls
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '300px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="styleLogo">Style Icon/Logo (shown in style selector on /create page):</label>
                        <input
                            type="file"
                            id="styleLogo"
                            accept="image/*"
                            onChange={handleLogoSelect}
                            style={{ marginBottom: '10px' }}
                        />
                        <p style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)', marginTop: '-5px', marginBottom: '10px' }}>
                            Square images work best (e.g., 200x200). Falls back to hardcoded logos if not set.
                        </p>
                        {logoPreview && (
                            <div className="logo-preview">
                                <img
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    style={{
                                        maxWidth: '100px',
                                        maxHeight: '100px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleRemoveLogo}
                                    style={{
                                        marginLeft: '10px',
                                        padding: '5px 10px',
                                        fontSize: '12px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                        {currentLogoUrl && !logoPreview && (
                            <div className="current-logo">
                                <p style={{ fontSize: '14px', color: 'var(--Gray-600, #475467)', margin: '5px 0' }}>
                                    Current logo:
                                </p>
                                <img
                                    src={currentLogoUrl}
                                    alt="Current logo"
                                    style={{
                                        maxWidth: '100px',
                                        maxHeight: '100px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '1px solid var(--Gray-300, #D0D5DD)'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <StyleLogoEditor
                        value={logoStyle}
                        onChange={setLogoStyle}
                        logoSrc={getLogoForStyle(name || currentTemplate?.name || '', logoPreview || currentLogoUrl)}
                        previewImageSrc={imagePreview || currentImageUrl}
                        portraitPreviewImageSrc={portraitImagePreview || currentPortraitImageUrl}
                        styleName={name || currentTemplate?.name || ''}
                    />
                    <div>
                        <label htmlFor="mapboxStyle">Mapbox Style (optional - leave empty to use default):</label>
                        <input 
                            type="text" 
                            id="mapboxStyle" 
                            value={mapboxStyle} 
                            onChange={(e) => setMapboxStyle(e.target.value)} 
                            placeholder="e.g., mapbox/streets-v12 or username/style-id"
                            style={{ marginBottom: '10px' }}
                        />
                        <p style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)', marginTop: '-5px', marginBottom: '15px' }}>
                            Examples: mapbox/satellite-v9, mapbox/outdoors-v12, or custom style IDs. Leave empty to use the default mythic style.
                        </p>
                    </div>
                    <div>
                        <label htmlFor="generationModel">Image Generation Model:</label>
                        <select 
                            id="generationModel" 
                            value={generationModel} 
                            onChange={(e) => setGenerationModel(e.target.value)}
                            style={{ 
                                width: '100%', 
                                padding: '8px', 
                                marginBottom: '10px',
                                borderRadius: '4px',
                                border: '1px solid var(--Gray-300, #D0D5DD)'
                            }}
                        >
                            <option value="gpt-image-1">Images Edit API (gpt-image-1)</option>
                            <option value="gpt-4o">Responses API (gpt-4o)</option>
                            <option value="gpt-4o-mini">Responses API (gpt-4o-mini)</option>
                            <option value="gpt-4.1">Responses API (gpt-4.1)</option>
                            <option value="gpt-4.1-mini">Responses API (gpt-4.1-mini)</option>
                            <option value="gpt-4.1-nano">Responses API (gpt-4.1-nano)</option>
                            <option value="o3">Responses API (o3)</option>
                            <option value="text-then-edit-4o">Text (gpt-4o) → Edit API</option>
                            <option value="text-then-edit-4.1">Text (gpt-4.1) → Edit API</option>
                            <option value="text-then-edit-o3">Text (o3) → Edit API</option>
                            <option value="flux-max">Flux Max (Replicate)</option>
                            <option value="gemini-flash">Gemini 2.5 Flash Image</option>
                            <option value="gemini-3-pro">Gemini 3 Pro Image</option>
                            <option value="gemini-3-flash">Gemini 3.1 Flash Image</option>
                        </select>
                        <p style={{ fontSize: '12px', color: 'var(--Gray-500, #667085)', marginTop: '-5px', marginBottom: '15px' }}>
                            Select the AI model to use for image generation. The Responses API models support revised prompts. Text → Edit models first generate an optimized prompt using the text model, then use it with the Edit API.
                        </p>
                    </div>
                    <div>
                        <label htmlFor="templateString">Template String:</label>
                        <textarea id="templateString" value={templateString} onChange={(e) => setTemplateString(e.target.value)} rows="15" required style={{ fontFamily: 'monospace' }}></textarea>
                        <div style={{ 
                            marginTop: '10px', 
                            padding: '15px', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '6px', 
                            border: '1px solid #dee2e6' 
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#495057' }}>Available Template Variables:</h4>
                            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#6c757d' }}>
                                <li><code>{'{{TITLE}}'}</code> - Activity name/title</li>
                                <li><code>{'{{DISTANCE_KM}}'}</code> - Distance in kilometers (e.g., "42.2")</li>
                                <li><code>{'{{DURATION_HMS}}'}</code> - Duration in hours/minutes/seconds (e.g., "2h 45m 30s")</li>
                                <li><code>{'{{ELEVATION_M}}'}</code> - Total elevation gain in meters</li>
                                <li><code>{'{{POWER_W}}'}</code> - Average power in watts (rides only)</li>
                                <li><code>{'{{PACE_PER_KM}}'}</code> - Pace per kilometer (runs only, replaces POWER_W line)</li>
                                <li><code>{'{{ACTIVITY_TYPE}}'}</code> - Type of activity (e.g., "Ride", "Run", "Hike")</li>
                                <li><code>{'{{ACTIVITY_TERRAIN}}'}</code> - Terrain type: "water" for swim/paddle activities, "land" for all others</li>
                                <li><code>{'{{WATER_PERCENTAGE}}'}</code> - Percentage of water in the map (e.g., "15.23")</li>
                            </ul>
                            <p style={{ margin: '10px 0 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                                Use <code>{'{{#STATS}}...{{/STATS}}'}</code> to wrap content that should only appear when stats are available.
                                The POWER_W line is automatically replaced with pace for running activities.
                            </p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                                Use <code>{'{{#AI_LOGO}}...{{/AI_LOGO}}'}</code> to wrap any legacy prompt text that asks the model to draw a logo into the map.
                                This block is currently disabled, so wrapped content will be stripped during generation.
                            </p>
                        </div>
                    </div>
                    <div>
                        <label>
                            <input type="checkbox" checked={requiresStats} onChange={(e) => setRequiresStats(e.target.checked)} />
                            Requires Strava Stats for population
                        </label>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Style Availability (based on route water content):
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input 
                                    type="checkbox" 
                                    checked={coastalOnly} 
                                    onChange={(e) => setCoastalOnly(e.target.checked)} 
                                />
                                <span>Coastal only - only show for routes with significant water content</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input 
                                    type="checkbox" 
                                    checked={landOnly} 
                                    onChange={(e) => setLandOnly(e.target.checked)} 
                                />
                                <span>Land only - only show for routes with minimal water content</span>
                            </label>
                        </div>
                        <p style={{ 
                            fontSize: '12px', 
                            color: 'var(--Gray-500, #667085)', 
                            marginTop: '8px',
                            fontStyle: 'italic'
                        }}>
                            Leave both unchecked to show this style for all route types. 
                            The water percentage threshold is configurable in admin settings.
                        </p>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Admin Only:
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '10px' }}>
                            <input 
                                type="checkbox" 
                                checked={adminOnly} 
                                onChange={(e) => setAdminOnly(e.target.checked)} 
                            />
                            <span>Admin only - only visible to admin accounts</span>
                        </label>
                        <p style={{ 
                            fontSize: '12px', 
                            color: 'var(--Gray-500, #667085)', 
                            marginTop: '8px',
                            fontStyle: 'italic',
                            marginLeft: '10px'
                        }}>
                            When checked, this prompt template will only be visible to admin users.
                        </p>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Test in Parallel:
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '10px' }}>
                            <input
                                type="checkbox"
                                checked={testInParallel}
                                onChange={(e) => setTestInParallel(e.target.checked)}
                            />
                            <span>Enable parallel test generation</span>
                        </label>
                        <p style={{
                            fontSize: '12px',
                            color: 'var(--Gray-500, #667085)',
                            marginTop: '8px',
                            fontStyle: 'italic',
                            marginLeft: '10px'
                        }}>
                            When checked, test prompts starting with this prompt's name will automatically run in parallel
                            (e.g., if this is "mythic", then "mythic test 1" will run alongside it).
                        </p>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Contour First:
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '10px' }}>
                            <input
                                type="checkbox"
                                checked={contourFirst}
                                onChange={(e) => setContourFirst(e.target.checked)}
                            />
                            <span>Enable contour preprocessing</span>
                        </label>
                        <p style={{
                            fontSize: '12px',
                            color: 'var(--Gray-500, #667085)',
                            marginTop: '8px',
                            fontStyle: 'italic',
                            marginLeft: '10px'
                        }}>
                            When checked, the input map will first be converted to a contour sketch using the selected
                            preprocessing model before being passed to the generation model. This can enhance artistic
                            effects for certain styles.
                        </p>
                    </div>
                    <div style={{ marginTop: '15px' }}>
                        <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                            Map Input Type:
                        </label>
                        <select
                            value={mapInputType}
                            onChange={(e) => {
                                const value = e.target.value;
                                setMapInputType(value);
                                if (value !== '3d') {
                                    setVariantOf('');
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '6px',
                                border: '1px solid var(--Gray-300, #D0D5DD)',
                                fontSize: '13px'
                            }}
                        >
                            <option value="2d">2D Map (standard)</option>
                            <option value="3d">3D Terrain Map</option>
                        </select>
                        <p style={{
                            fontSize: '12px',
                            color: 'var(--Gray-500, #667085)',
                            marginTop: '6px',
                            fontStyle: 'italic'
                        }}>
                            Choose whether this prompt expects the classic 2D map or the high-resolution 3D terrain capture as input.
                        </p>
                    </div>
                    {mapInputType === '3d' && (
                        <div style={{ marginTop: '12px' }}>
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                                Linked 2D Prompt:
                            </label>
                            <select
                                value={variantOf}
                                onChange={(e) => setVariantOf(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--Gray-300, #D0D5DD)',
                                    fontSize: '13px'
                                }}
                            >
                                <option value="">Select a 2D prompt to pair with</option>
                                {templates
                                    .filter((templateOption) => templateOption.id !== currentTemplate?.id && (templateOption.mapInputType || '2d') === '2d')
                                    .map((templateOption) => (
                                        <option key={templateOption.id} value={templateOption.id}>
                                            {templateOption.name || templateOption.id}
                                        </option>
                                    ))}
                            </select>
                            <p style={{
                                fontSize: '12px',
                                color: 'var(--Gray-500, #667085)',
                                marginTop: '6px',
                                fontStyle: 'italic'
                            }}>
                                Link this 3D variant to its 2D counterpart so it can be presented as a single style choice in the app.
                            </p>
                        </div>
                    )}
                    {contourFirst && (
                        <div style={{ marginTop: '12px' }}>
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                                Contour Model:
                            </label>
                            <select
                                value={contourModel}
                                onChange={(e) => setContourModel(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--Gray-300, #D0D5DD)',
                                    fontSize: '13px'
                                }}
                            >
                                <option value="flux">Flux (Replicate)</option>
                                <option value="gemini">Gemini 2.5 Flash</option>
                                <option value="gemini-3-pro">Gemini 3 Pro</option>
                                <option value="gemini-3-flash">Gemini 3.1 Flash</option>
                            </select>
                            <p style={{
                                fontSize: '12px',
                                color: 'var(--Gray-500, #667085)',
                                marginTop: '6px',
                                fontStyle: 'italic'
                            }}>
                                Choose which model prepares the contour sketch. Flux runs on Replicate, while Gemini options use Google's AI image models.
                            </p>
                        </div>
                    )}
                    {contourFirst && (
                        <div style={{ marginTop: '12px' }}>
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                                Contour Prompt:
                            </label>
                            <textarea
                                value={contourPrompt}
                                onChange={(e) => setContourPrompt(e.target.value)}
                                rows={5}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--Gray-300, #D0D5DD)',
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    minHeight: '120px'
                                }}
                                placeholder={`Enter the instruction sent to ${contourModelShortName} when preprocessing the contour image`}
                            />
                            <p style={{
                                fontSize: '12px',
                                color: 'var(--Gray-500, #667085)',
                                marginTop: '6px',
                                fontStyle: 'italic'
                            }}>
                                This prompt is sent to {contourModelShortName} before the artistic model runs. Adjust it to fine-tune the
                                contour sketch that seeds the final image.
                            </p>
                        </div>
                    )}
                    {statsValidationError && <p className="error stats-validation-error">{statsValidationError}</p>} {/* Display stats validation error */}
                    <div className="form-actions">
                        <button type="submit" disabled={isUploadingImage || isUploadingPortraitImage || isUploadingVideo || isUploadingPortraitVideo || isUploadingLogo}>
                            {isUploadingImage || isUploadingPortraitImage || isUploadingVideo || isUploadingPortraitVideo || isUploadingLogo ? 'Uploading...' : 'Save Template'}
                        </button>
                        <button type="button" onClick={closeForm} style={{ marginLeft: '10px', backgroundColor: '#6c757d' }}>Cancel</button>
                    </div>
                    {error && !isLoading && <p className="error">{error}</p>} {/* Show form-specific errors here */}
                        </form>
                    </div>
                </div>
            )}

            <h2>Existing Templates</h2>
            {isLoading && <p>Loading templates...</p>}
            {!isLoading && error && !showForm && <p className="error">{error}</p>} {/* Show general loading error only if form isn't up with its own error */}
            {!isLoading && templates.length === 0 && !error && <p>No prompt templates found. Create one!</p>}
            {!isLoading && templates.length > 0 && (
                <ul className="templates-list" style={{ listStyleType: 'none', padding: 0 }}>
                    {templates.map((template, index) => {
                        // Check if this template is a child of any parent template
                        const templateName = template.name || '';
                        let isChild = false;
                        
                        // Look through all templates to see if any is a parent of this one
                        for (const potentialParent of templates) {
                            if (potentialParent.name && 
                                potentialParent.name !== templateName &&
                                templateName.startsWith(potentialParent.name + ' ')) {
                                isChild = true;
                                break;
                            }
                        }
                        
                        return (
                        <li key={template.id} className={isChild ? 'child-template' : 'parent-template'} style={{ border: '1px solid #eee', padding: '10px', marginBottom: '10px', borderRadius: '5px', marginLeft: isChild ? '40px' : '0' }}>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                                {template.exampleImageUrl && (
                                    <div style={{ flexShrink: 0 }}>
                                        <img
                                            src={template.exampleImageUrl}
                                            alt={`${template.name} example`}
                                            style={{
                                                width: '100px',
                                                height: '60px',
                                                objectFit: 'cover',
                                                borderRadius: '6px',
                                                border: '1px solid var(--Gray-300, #D0D5DD)'
                                            }}
                                        />
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    <h3>{template.name}</h3>
                                    <p>{template.description || 'No description.'}</p>
                                    <p style={{ margin: '2px 0' }}><small>Requires Stats: {template.requiresStravaStats ? 'Yes' : 'No'}</small></p>
                                    {template.coastalOnly && (
                                        <p style={{ margin: '2px 0' }}><small style={{ color: 'var(--Primary-600, #57534E)', fontWeight: '600' }}>
                                            🌊 Coastal only
                                        </small></p>
                                    )}
                                    {template.landOnly && (
                                        <p style={{ margin: '2px 0' }}><small style={{ color: 'var(--Primary-600, #57534E)', fontWeight: '600' }}>
                                            🏔️ Land only
                                        </small></p>
                                    )}
                                    {template.adminOnly && (
                                        <p style={{ margin: '2px 0' }}><small style={{ color: '#DC3545', fontWeight: '600' }}>
                                            🔒 Admin only
                                        </small></p>
                                    )}
                                    {template.testInParallel && (
                                        <p style={{ margin: '2px 0' }}><small style={{ color: '#6366F1', fontWeight: '600' }}>
                                            🧪 Test in parallel
                                        </small></p>
                                    )}
                                    {template.contourFirst && (
                                        <p style={{ margin: '2px 0' }}><small style={{ color: '#10B981', fontWeight: '600' }}>
                                            🎨 Contour first (Model: {template.contourModel ? template.contourModel.charAt(0).toUpperCase() + template.contourModel.slice(1) : 'flux'})
                                        </small></p>
                                    )}
                                    {template.tags && template.tags.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', margin: '4px 0' }}>
                                            {template.tags.map(tag => (
                                                <span key={tag} style={{
                                                    padding: '1px 8px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    backgroundColor: '#f0ebe3',
                                                    color: '#57534E',
                                                    border: '1px solid #d6cfc4',
                                                }}>
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {(template.exampleImageUrl || template.portraitExampleImageUrl) && (
                                        <p style={{ margin: '2px 0' }}><small>
                                            Example image: {[template.exampleImageUrl && 'Landscape', template.portraitExampleImageUrl && 'Portrait'].filter(Boolean).join(', ')}
                                        </small></p>
                                    )}
                                    {(template.transitionVideoUrl || template.portraitTransitionVideoUrl) && (
                                        <p style={{ margin: '2px 0' }}><small style={{ color: '#8B5CF6', fontWeight: '600' }}>
                                            🎬 Transition video: {[template.transitionVideoUrl && 'Landscape', template.portraitTransitionVideoUrl && 'Portrait'].filter(Boolean).join(', ')}
                                        </small></p>
                                    )}
                                    {template.generationModel && (
                                        <p style={{ margin: '2px 0' }}><small>Model: {template.generationModel}</small></p>
                                    )}
                                </div>
                            </div>
                            <div className="template-actions" style={{ marginTop: '10px' }}>
                                <button onClick={() => handleEdit(template)} style={{ marginRight: '10px' }}>Edit</button>
                                <button onClick={() => handleDuplicate(template)} style={{ marginRight: '10px' }}>Duplicate</button>
                                <button onClick={() => handleDelete(template.id)} style={{ backgroundColor: '#dc3545' }}>Delete</button>
                            </div>
                        </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default PromptManagementPage; 
